import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import { FiPlus, FiEdit2, FiTrash2, FiImage, FiRotateCcw, FiTag, FiSearch } from "react-icons/fi";
import { useDropzone } from "react-dropzone";
import { baseurl, imgurl } from "../baseurl";
import axios from "axios";
import Swal from "sweetalert2";

function BrandManagement() {
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState({ category_id: "", name: "", description: "", logo: null });
  const [preview, setPreview] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [loading, setLoading] = useState(false);

  const showToast = (title, icon = "success") => {
    Swal.fire({ toast: true, position: "top-end", icon, title, showConfirmButton: false, timer: 2000 });
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${baseurl}getCategories`);
      setCategories(res.data.categories || []);
    } catch (err) { showToast("Category fetch failed", "error"); }
  };

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${baseurl}getAllBrands`);
      setBrands(res.data.brands || []);
    } catch (err) { showToast("Brand fetch failed", "error"); }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setFormData((prev) => ({ ...prev, logo: file }));
      setPreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const resetForm = () => {
    setEditingBrand(null);
    setFormData({ category_id: "", name: "", description: "", logo: null });
    setPreview(null);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.category_id) return showToast("Name and Category required", "error");

    const form = new FormData();
    form.append("category_id", formData.category_id);
    form.append("name", formData.name);
    form.append("description", formData.description);
    if (formData.logo) form.append("logo", formData.logo);

    try {
      setLoading(true);
      if (editingBrand) {
        await axios.put(`${baseurl}updateBrand/${editingBrand.brand_id}`, form);
        showToast("Brand updated");
      } else {
        await axios.post(`${baseurl}addBrand`, form);
        showToast("Brand added");
      }
      resetForm();
      fetchBrands();
    } catch (err) { showToast("Operation failed", "error"); }
    finally { setLoading(false); }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ category_id: brand.category_id, name: brand.brand_name, description: brand.description || "", logo: null });
    setPreview(brand.logo ? `${imgurl}${brand.logo}` : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (brand_id) => {
    const confirm = await Swal.fire({ title: "Delete Brand?", icon: "warning", showCancelButton: true });
    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteBrand/${brand_id}`);
        showToast("Deleted");
        fetchBrands();
      } catch (err) { showToast("Delete failed", "error"); }
    }
  };

  const filteredBrands = brands.filter(b => b.brand_name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          <div className="row g-3">
            {/* LEFT: COMPACT FORM */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm sticky-top" style={{ top: '80px' }}>
                <div className={`card-header border-0 py-3 ${editingBrand ? 'bg-warning text-dark' : 'bg-primary text-white'}`}>
                  <h6 className="mb-0 fw-bold">{editingBrand ? <><FiEdit2 className="me-2"/>Edit Brand</> : <><FiPlus className="me-2"/>New Brand</>}</h6>
                </div>
                <div className="card-body p-3">
                  <div className="mb-3 text-center">
                    <div {...getRootProps()} className={`upload-zone border-2 rounded ${isDragActive ? 'bg-soft-primary' : ''}`} style={{ borderStyle: 'dashed', cursor: 'pointer' }}>
                      <input {...getInputProps()} />
                      {preview ? (
                        <div className="position-relative d-inline-block p-2">
                          <img src={preview} alt="Preview" width="80" height="80" className="rounded shadow-sm object-fit-cover" />
                          <div className="upload-overlay small">Change</div>
                        </div>
                      ) : (
                        <div className="py-4 text-muted">
                          <FiImage size={24} className="mb-1" />
                          <p className="extra-small mb-0">Logo (Drag & Drop)</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="extra-small fw-bold text-muted text-uppercase">Brand Identity</label>
                    <input type="text" className="form-control form-control-sm" name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Nike" />
                  </div>
                  <div className="mb-2">
                    <label className="extra-small fw-bold text-muted text-uppercase">Parent Category</label>
                    <select className="form-select form-select-sm" name="category_id" value={formData.category_id} onChange={handleChange}>
                      <option value="">Select...</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="extra-small fw-bold text-muted text-uppercase">Notes</label>
                    <textarea className="form-control form-control-sm" rows="2" name="description" value={formData.description} onChange={handleChange} placeholder="Brief summary..."></textarea>
                  </div>
                  <div className="d-flex gap-2">
                    <button className={`btn btn-sm flex-grow-1 fw-bold ${editingBrand ? 'btn-warning' : 'btn-primary'}`} onClick={handleSubmit} disabled={loading}>
                      {loading ? '...' : editingBrand ? 'Update' : 'Save'}
                    </button>
                    <button className="btn btn-sm btn-light border" onClick={resetForm}><FiRotateCcw /></button>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: BRAND LIST */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold mb-0">Brand Directory</h6>
                  <div className="input-group input-group-sm w-auto shadow-sm">
                    <span className="input-group-text bg-white border-end-0"><FiSearch className="text-muted"/></span>
                    <input type="text" className="form-control border-start-0" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 custom-table">
                    <thead className="bg-light">
                      <tr className="extra-small text-muted text-uppercase">
                        <th className="ps-3">Identity</th>
                        <th>Category</th>
                        <th>Summary</th>
                        <th className="text-end pe-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBrands.map((b) => (
                        <tr key={b.brand_id}>
                          <td className="ps-3">
                            <div className="d-flex align-items-center">
                              {b.logo ? (
                                <img src={`${imgurl}${b.logo}`} alt="" width="36" height="36" className="rounded-circle border me-2 object-fit-cover shadow-xs" />
                              ) : (
                                <div className="avatar-placeholder me-2">{b.brand_name.charAt(0)}</div>
                              )}
                              <span className="fw-bold text-dark small">{b.brand_name}</span>
                            </div>
                          </td>
                          <td><span className="badge bg-soft-primary text-primary border-0 fw-normal"><FiTag className="me-1"/>{b.category_name}</span></td>
                          <td><div className="text-truncate text-muted extra-small" style={{maxWidth: '180px'}}>{b.description || "No notes available"}</div></td>
                          <td className="text-end pe-3">
                            <div className="btn-group shadow-sm border rounded bg-white">
                              <button className="btn btn-xs btn-white border-0 py-1 px-2" onClick={() => handleEdit(b)}><FiEdit2 size={12} className="text-primary"/></button>
                              <button className="btn btn-xs btn-white border-0 border-start py-1 px-2" onClick={() => handleDelete(b.brand_id)}><FiTrash2 size={12} className="text-danger"/></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 10px; font-weight: 700; letter-spacing: 0.03em; }
        .bg-soft-primary { background-color: #eef2ff !important; color: #4338ca !important; }
        .custom-table tbody td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .avatar-placeholder { width: 36px; height: 36px; background: #f1f5f9; color: #64748b; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; }
        .upload-zone:hover { background: #f8fafc; border-color: #3b82f6 !important; }
        .upload-overlay { position: absolute; bottom: 0; width: 100%; background: rgba(0,0,0,0.5); color: white; border-radius: 0 0 5px 5px; opacity: 0; transition: 0.3s; }
        .position-relative:hover .upload-overlay { opacity: 1; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
      `}</style>
    </>
  );
}

export default BrandManagement;